import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';

/**
 * Status type for shift definitions
 */
export type ShiftDefinitionStatusType = 'active' | 'inactive';

/**
 * Data structure for ShiftDefinitionListRow component
 */
export interface ShiftDefinitionListRowData {
  id: string;
  name: string | null;
  startTime: string | null;
  endTime: string | null;
  breakMinutes: number | null;
  status: ShiftDefinitionStatusType;
  /** Pre-formatted time range string (e.g., "08:00 – 16:00") */
  formattedTimeRange?: string;
  /** Pre-formatted break minutes string */
  formattedBreak?: string;
  /** Resolved role name (not ID) */
  roleName?: string | null;
  /** Status label (translated) */
  statusLabel?: string;
  color?: string | null;
  icon?: string | null;
}

/**
 * ShiftDefinitionListRow Component
 *
 * Displays a shift definition's information in a horizontal row format for list views.
 * Shows name, status badge, time range, break minutes, role name, and action icons.
 *
 * @example
 * ```html
 * <zag-shift-definition-list-row
 *   [shiftDefinition]="shiftDefinitionData"
 *   [editRoute]="['/shift-definitions', shiftDefinitionData.id]"
 *   (rowClick)="onShiftDefinitionSelected($event)"
 *   (deleteClick)="onDeleteShiftDefinition($event)">
 * </zag-shift-definition-list-row>
 * ```
 */
@Component({
  selector: 'zag-shift-definition-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon],
  host: {
    '[class.zag-shift-definition-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <div class="zag-shift-definition-list-row__content">
      <div
        class="zag-shift-definition-list-row__icon-avatar zag-icon-surface-gradient"
        [class.zag-shift-definition-list-row__icon-avatar--fallback]="!shiftDefinition().color"
        [style.background-color]="shiftDefinition().color || null"
      >
        <span
          class="zag-shift-definition-list-row__icon"
          [zagIcon]="shiftDefinition().icon || 'schedule'"
          [style.color]="getIconColor(shiftDefinition().color)"
        ></span>
      </div>
      <div class="zag-shift-definition-list-row__info">
        <div class="zag-shift-definition-list-row__title">
          <span class="zag-shift-definition-list-row__name">{{ shiftDefinition().name }}</span>
          <span
            class="zag-shift-definition-list-row__status"
            [class.zag-shift-definition-list-row__status--inactive]="shiftDefinition().status === 'inactive'"
          >
            {{ shiftDefinition().statusLabel || shiftDefinition().status }}
          </span>
        </div>
        <div class="zag-shift-definition-list-row__meta">
          @if (shiftDefinition().formattedTimeRange) {
            <span>{{ shiftDefinition().formattedTimeRange }}</span>
          }
          @if (breakLabel() && shiftDefinition().formattedBreak) {
            <span>{{ breakLabel() }} {{ shiftDefinition().formattedBreak }}</span>
          }
          @if (roleLabel() && shiftDefinition().roleName !== undefined) {
            <span>{{ roleLabel() }} {{ shiftDefinition().roleName || noRoleLabel() }}</span>
          }
        </div>
      </div>
    </div>

    <div class="zag-shift-definition-list-row__actions">
      @if (showDelete()) {
        <button
          type="button"
          class="zag-shift-definition-list-row__action zag-shift-definition-list-row__action--delete"
          [disabled]="deleteDisabled()"
          [attr.aria-label]="deleteAriaLabel() || null"
          (click)="onDeleteClick($event)"
        >
          <span zagIcon="delete"></span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .zag-shift-definition-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        cursor: pointer;
        transition: box-shadow 0.15s ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-shift-definition-list-row__content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        min-width: 0;
      }

      .zag-shift-definition-list-row__icon {
        font-size: var(--entity-avatar-icon-size-list);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .zag-shift-definition-list-row__icon-avatar {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .zag-shift-definition-list-row__icon-avatar--fallback {
        background: linear-gradient(135deg, var(--color-primary), var(--color-primary-variant));
      }

      .zag-shift-definition-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-shift-definition-list-row__title {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
      }

      .zag-shift-definition-list-row__name {
        font-weight: 600;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-shift-definition-list-row__status {
        font-size: 0.75rem;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: var(--border-radius-sm);
        background-color: var(--color-success-light);
        color: var(--color-success);
        white-space: nowrap;
        flex-shrink: 0;
      }

      .zag-shift-definition-list-row__status--inactive {
        background-color: var(--color-warning-light);
        color: var(--color-warning-dark);
      }

      .zag-shift-definition-list-row__meta {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-md);
        color: var(--text-color-secondary);
        font-size: var(--font-size-sm);
      }

      .zag-shift-definition-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .zag-shift-definition-list-row__action {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-decoration: none;
        transition:
          background-color 0.15s,
          color 0.15s;

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          &:hover {
            background-color: transparent;
            color: var(--text-color-secondary);
          }
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-shift-definition-list-row__action--delete {
        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }
      }
    `,
  ],
})
export class ShiftDefinitionListRow {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The shift definition data to display */
  readonly shiftDefinition = input.required<ShiftDefinitionListRowData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Whether to show the delete button */
  readonly showDelete = input(true);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Label prefix for break minutes (e.g., "Break:") */
  readonly breakLabel = input('');

  /** Label prefix for role name (e.g., "Role:") */
  readonly roleLabel = input('');

  /** Label when no role is assigned */
  readonly noRoleLabel = input('—');

  /** Emitted when the row is clicked */
  readonly rowClick = output<ShiftDefinitionListRowData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<ShiftDefinitionListRowData>();

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.shiftDefinition());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.shiftDefinition());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
